import React, { useEffect, useState } from 'react';
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  Image,
  Modal,
} from 'antd';
import { HomeOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { createStyles } from 'antd-style';
import { theme } from 'antd';
import { apiRequest } from '../../../../service/apiRequest';
import { showSuccess, showError } from '../../../../components/errorHandler';
import { useCurrentModule } from '../../../../hooks/useCurrentModule';
import type { TableProps, PopconfirmProps, TableColumnsType } from 'antd';
import type { BodyParts } from '../../../../types/BodyParts';
import BodyPartsModal from './BodyPartsModal';
import BodyPartProducttypeAllotmentModal from './BodyPartProducttypeAllotmentModal';

const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table-container {
      .ant-table-body,
      .ant-table-content {
        scrollbar-width: thin;
        scrollbar-color: #eaeaea transparent;
      }
    }
  `,
}));

const BodyPartsPage: React.FC = () => {
  const [bodyParts, setBodyParts] = useState<BodyParts[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<BodyParts | null>(null);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [isVideoModalVisible, setIsVideoModalVisible] = useState(false);
  const [videoSrc, setVideoSrc] = useState<string | null>(null);
  const { styles } = useStyle();
  const currentModule = useCurrentModule();

  const [isAllotmentModalVisible, setIsAllotmentModalVisible] = useState(false);
  const [selectedBodyPartId, setSelectedBodyPartId] = useState<number | null>(null);
  const [productTypes, setProductTypes] = useState<string[]>([]); // list of all product types
  const [existingAllotments, setExistingAllotments] = useState<any[]>([]);


  const cancel: PopconfirmProps['onCancel'] = () => {
    showError('Click on No');
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchBodyParts = async () => {
    try {
      const data = await apiRequest<BodyParts[]>({
        method: 'get',
        url: 'body-parts',
      });
      setBodyParts(data);
    } catch {
      showError('Failed to fetch body parts');
    }
  };

  useEffect(() => {
    fetchBodyParts();
  }, []);

  const handleEditClick = (record: BodyParts) => {
    setIsEditMode(true);
    setInitialValues(record);
    setIsModalVisible(true);
  };

  const handleCreate = () => fetchBodyParts();

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const showVideoModal = (src: string) => {
    setVideoSrc(src.startsWith("http") ? src : `https://${src}`);
    setIsVideoModalVisible(true);
  };

  const hideVideoModal = () => {
    setVideoSrc(null);
    setIsVideoModalVisible(false);
  };


  const showAllotmentModal = async (bp_id: number) => {
    setSelectedBodyPartId(bp_id);
    try {
      // Fetch product types for this body part
      const types = await apiRequest<string[]>({
        method: 'get',
        url: `product-body-parts/`, // API that returns all product types
      });
      setProductTypes(types);

      // Fetch existing allotments for this body part
      const allotments = await apiRequest<any[]>({
        method: 'get',
        url: `body-parts-product-type-allotment/${bp_id}`,
      });
      setExistingAllotments(allotments);

      setIsAllotmentModalVisible(true);
    } catch (error) {
      showError('Failed to fetch product types/allotments');
    }
  };

  const hideAllotmentModal = () => {
    setIsAllotmentModalVisible(false);
    setSelectedBodyPartId(null);
  };

  const handleDelete = async (bp_id: number) => {
    try {
      await apiRequest({
        method: 'delete',
        url: `/delete-body-part/${bp_id}`,
      });
      showSuccess('Body Part deleted');
      fetchBodyParts();
    } catch {
      showError('Failed to delete body part');
    }
  };

  const handleStatusToggle = async (bp_id: number) => {
    setBodyParts((prev) =>
      prev.map((item) =>
        item.bp_id === bp_id
          ? { ...item, bp_status: item.bp_status === 1 ? 0 : 1 }
          : item
      )
    );

    try {
      await apiRequest({
        method: 'put',
        url: `/update-status-body-part/${bp_id}`,
      });
      showSuccess('Body Part status updated');
    } catch {
      showError('Failed to update status');
      // Optionally revert UI change on failure
      fetchBodyParts();
    }
  };

  const convertToEmbedUrl = (url: string) => {
    const youtubeRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/;
    const match = url.match(youtubeRegex);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const columns: TableColumnsType<BodyParts> = [
    {
      title: 'Image',
      dataIndex: 'bp_image',
      key: 'bp_image',
      width: 100,
      render: (_, record) =>
        record.bp_image ? (
          <Image
            width={40}
            height={50}
            src={record.bp_image}
            alt="body part"
            style={{ objectFit: 'cover', borderRadius: 8 }}
            preview={{
              mask: 'Click to Preview',
            }}
          />
        ) : (
          '-'
        ),
    },
    {
      title: 'Body Part Name',
      dataIndex: 'bp_name',
      key: 'bp_name',
      width: 300,
      sorter: (a, b) => a.bp_name.localeCompare(b.bp_name),
      render: (_, record) => <div>{record.bp_name}</div>,
    },
    {
      title: 'Unit',
      dataIndex: 'bp_unit',
      key: 'bp_unit',
      width: 100,
      sorter: (a, b) => a.bp_unit.localeCompare(b.bp_unit),
      render: (_, record) => <div>{record.bp_unit}</div>,
    },
    {
      title: "Video",
      dataIndex: "bp_video",
      key: "bp_video",
      width: 200,
      render: (_, record) =>
        record.bp_video ? (
          <Button type="link" onClick={() => showVideoModal(record.bp_video)}>
            View Video
          </Button>
        ) : (
          "-"
        ),
    },

  ];

  if (currentModule?.right_edit === 'Y' || currentModule?.right_delete === 'Y') {
    columns.push({
      title: 'Action',
      width: 200,
      render: (_, record) => (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <Space size="middle">
            {currentModule?.right_edit === 'Y' && (
              <>
                <Switch
                  checked={!!record.bp_status}
                  onChange={() => handleStatusToggle(record.bp_id)}
                />
                <Button onClick={() => handleEditClick(record)}>
                  <EditOutlined />
                </Button>
              </>
            )}
            {currentModule?.right_delete === 'Y' && (
              <Popconfirm
                title="Delete this body part?"
                onConfirm={() => handleDelete(record.bp_id)}
                onCancel={cancel}
                okText="Yes"
                cancelText="No"
              >
                <Button danger>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            )}
          </Space>

          <Space size="middle">
            <Button type="primary" onClick={() => showAllotmentModal(record.bp_id)}>
              View Product Type
            </Button>
          </Space>
        </div>
      ),
    });
  }


  return (
    <>
      <Space
        style={{
          marginBottom: 16,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <Breadcrumb
          items={[
            {
              title: <a href="/dashboard">Home</a>,
            },
            {
              title: 'View Body Parts',
            },
          ]}
        />

        {currentModule?.right_add === 'Y' && (
          <Button type="primary" onClick={showCreateModal}>
            Add Body Part
          </Button>
        )}
      </Space>

      <div style={{ background: colorBgContainer, borderRadius: borderRadiusLG }}>
        <Table<BodyParts>
          tableLayout="fixed"
          className={styles.customTable}
          columns={columns}
          dataSource={bodyParts.slice(
            (pagination.current - 1) * pagination.pageSize,
            pagination.current * pagination.pageSize
          )}
          rowKey="bp_id"
          pagination={false}
          scroll={{ y: 37 * 17 }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–{' '}
                {Math.min(pagination.current * pagination.pageSize, bodyParts.length)} of{' '}
                {bodyParts.length}
              </span>
              <Pagination
                {...pagination}
                total={bodyParts.length}
                showSizeChanger
                onChange={(page, pageSize) => setPagination({ current: page, pageSize })}
              />
            </Flex>
          )}
        />
      </div>

      <BodyPartsModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
        bodyParts={bodyParts}
      />
      <Modal
        visible={isVideoModalVisible}
        onCancel={hideVideoModal}
        footer={null}
        width="100%"
        style={{ top: 0, padding: 0 }}
        bodyStyle={{ padding: 0, height: "90vh" }}
        centered
        closable
        destroyOnClose
      >
        {videoSrc && (
          videoSrc.includes("youtube.com") || videoSrc.includes("youtu.be") ? (
            <iframe
              width="98%"
              height="98%"
              src={convertToEmbedUrl(videoSrc)}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          ) : (
            <video
              src={videoSrc}
              controls
              autoPlay
              style={{ width: "90%", height: "90%" }}
            />
          )
        )}
      </Modal>

      <BodyPartProducttypeAllotmentModal
        visible={isAllotmentModalVisible}
        onCancel={hideAllotmentModal}
        bodyPartId={selectedBodyPartId}
        existingAllotments={existingAllotments} // keep this
        onSave={fetchBodyParts}
      />


    </>
  );
};

export default BodyPartsPage;
